import { CONTENTTYPE_PERMISSIONS_MAP, UserPermissions, UserSubscription } from '@creditsights/api-ui-web';
import {
  colors,
  DataViewConfig,
  ExpandedViewConfig,
  Icon,
  ICONS,
  TabData,
  TableColumns,
  TableViewConfig,
} from '@creditsights/styleguide-web';
import { InitialActionAllower, RouteToCallback } from '@creditsights/styleguide-web/components/DataView';
import { MouseEvent } from 'react';
import { ROUTES } from '../../Base.constants';
import {
  ArticleTitleSummaryFormatter,
  DateRenderer,
  GhostLinkRenderer,
  TitleWithContextMenuRenderer,
} from '../../components/formatters';
import { ContentTypeRenderer } from '../../components/formatters/ContentTypeRenderer';
import { ContextMenuRenderer } from '../../components/formatters/ContextMenuRenderer';
import { SummaryRenderer } from '../../components/formatters/SummaryRenderer';
import { LockedArticleLabel } from '../../components/RestrictedArticleAccess/LockedArticle';
import { ContentTypeFilter, ContentTypeFilterToContentType, ContentTypeTag, IssuerType } from '../../models';
import { ArticleTableRow } from '../../models/ArticleTableRow';
import { TagsFilterParams } from '../../services/tags/tags.model';
import {
  ARTICLE_TYPE_COLUMN_WIDTH,
  CONTEXT_MENU_COLUMN_WIDTH,
  DATE_COLUMN_WIDTH,
} from '../../utils/columnConfigConstants';

const expandedConfig = (
  isTypeShown: boolean,
  routeToCallback: RouteToCallback,
  shouldAllowInitialAction: InitialActionAllower,
  userPermissions: UserPermissions
): ExpandedViewConfig => {
  const articleType = {
    dataKey: 'articleType',
    dataRenderer: ContentTypeRenderer,
    dataViewItemConfiguration: { shouldAllowInitialAction, routeToCallback },
  };

  const subtitle = [
    {
      renderWithoutDataValue: true,
      dataRenderer: GhostLinkRenderer,
      dataViewItemConfiguration: { shouldAllowInitialAction, routeToCallback },
    },
    {
      dataKey: 'publishDate',
      dataRenderer: DateRenderer,
      dataViewItemConfiguration: { shouldAllowInitialAction, routeToCallback },
    },
  ];

  return {
    title: {
      dataKey: 'content.title',
      dataRenderer: TitleWithContextMenuRenderer,
      dataViewItemConfiguration: { shouldAllowInitialAction, routeToCallback },
    },
    subtitle: isTypeShown ? [articleType, ...subtitle] : subtitle,
    content: {
      dataKey: 'content.summary',
      dataRenderer: SummaryRenderer,
      dataViewItemConfiguration: { shouldAllowInitialAction, routeToCallback },
    },
    disabled: {
      dataViewItemConfiguration: { shouldAllowInitialAction, routeToCallback },
      dataRendererProps: { userPermissions },
    },
  };
};

const columnsConfig = (
  isTypeShown: boolean,
  routeToCallback: RouteToCallback,
  shouldAllowInitialAction: InitialActionAllower,
  userPermissions: UserPermissions
): TableViewConfig => {
  const columns: TableColumns[] = [
    {
      dataKey: 'content',
      cellLinkRenderer: GhostLinkRenderer,
      cellDataRenderer: ArticleTitleSummaryFormatter,
      dataViewItemConfiguration: { shouldAllowInitialAction, routeToCallback },
    },
    {
      dataKey: 'publishDate',
      cellDataRenderer: DateRenderer,
      width: DATE_COLUMN_WIDTH,
      dataViewItemConfiguration: { shouldAllowInitialAction, routeToCallback },
    },
    {
      dataKey: 'id',
      cellDataRenderer: ContextMenuRenderer,
      width: CONTEXT_MENU_COLUMN_WIDTH,
      stopMouseUpPropagation: true,
    },
  ];

  if (isTypeShown) {
    columns.splice(1, 0, {
      dataKey: 'articleType',
      width: ARTICLE_TYPE_COLUMN_WIDTH,
      cellDataRenderer: ContentTypeRenderer,
      dataViewItemConfiguration: { shouldAllowInitialAction, routeToCallback },
    });
  }

  return {
    columns,
    disabled: {
      dataViewItemConfiguration: { shouldAllowInitialAction, routeToCallback },
      dataRendererProps: { userPermissions },
    },
  };
};

export const viewConfig = (
  onArticleClick: (article: ArticleTableRow, event: MouseEvent<HTMLElement>) => void,
  isTypeShown: boolean,
  userPermissions: UserPermissions,
  isPreviewable?: boolean
): DataViewConfig<ArticleTableRow> => {
  const routeToCallback: RouteToCallback = (articleId) => ROUTES.article(articleId);
  const shouldAllowInitialAction: InitialActionAllower = (row) => isPreviewable || !!row?.isDisabled;

  return {
    tableViewConfig: columnsConfig(isTypeShown, routeToCallback, shouldAllowInitialAction, userPermissions),
    expandedViewConfig: expandedConfig(isTypeShown, routeToCallback, shouldAllowInitialAction, userPermissions),
    onRowMouseUp: onArticleClick,
    getIsDisabled: (article: ArticleTableRow) => !article.canAccess,
    disabledItemElement: LockedArticleLabel,
  };
};

const RecentlyPublishedFilterLabel = {
  [ContentTypeFilter.ALL]: 'All',
  [ContentTypeFilter.RESEARCH]: 'Research',
  [ContentTypeFilter.NEWS]: 'News',
  [ContentTypeFilter.COVENANTS]: 'Covenants',
};

const getArticleDataTestId = (articleTypeFilter: ContentTypeFilter) =>
  `article-type-${articleTypeFilter.toLowerCase()}`;

const getRecentlyPublishedFilterTab = (
  selectedArticleType: ContentTypeFilter,
  articleType: ContentTypeFilter,
  setArticleType: (articleType: ContentTypeFilter) => void,
  permissions: string[] | null
): TabData => {
  const contentType = ContentTypeFilterToContentType[articleType];
  const icon =
    contentType && permissions && !permissions.includes(CONTENTTYPE_PERMISSIONS_MAP[contentType])
      ? ICONS.LockOutline
      : undefined;
  const activeType = selectedArticleType === articleType;
  return {
    active: activeType,
    onClick: () => setArticleType(articleType),
    label: RecentlyPublishedFilterLabel[articleType],
    children: icon ? (
      <Icon
        name={ICONS.LockOutline}
        width={12}
        height={12}
        color={activeType ? colors.primary800 : colors.tertiary700}
      />
    ) : undefined,
    dataTestId: getArticleDataTestId(articleType),
  };
};

export const getRecentlyPublishedTabs = (
  selectedArticleType: ContentTypeFilter,
  setArticleType: (articleType: ContentTypeFilter) => void,
  permissions: string[] | null
): TabData[] =>
  Object.keys(ContentTypeFilter).map((key) =>
    getRecentlyPublishedFilterTab(selectedArticleType, ContentTypeFilter[key], setArticleType, permissions)
  );

export const getContentTypeTagFilter = (articleType?: ContentTypeFilter): TagsFilterParams => {
  return [contentTypeFiltersMap[articleType || ContentTypeFilter.ALL]];
};

export const contentTypeFiltersMap: Record<ContentTypeFilter, ContentTypeTag[]> = {
  [ContentTypeFilter.ALL]: [ContentTypeTag.News, ContentTypeTag.Covenants, ContentTypeTag.Research],
  [ContentTypeFilter.NEWS]: [ContentTypeTag.News],
  [ContentTypeFilter.COVENANTS]: [ContentTypeTag.Covenants],
  [ContentTypeFilter.RESEARCH]: [ContentTypeTag.Research],
};

export const getInitialActiveTab = (subscription: UserSubscription, issuerType?: IssuerType): ContentTypeFilter => {
  if (issuerType === IssuerType.Municipal) {
    return ContentTypeFilter.RESEARCH;
  }
  switch (subscription) {
    case UserSubscription.CS:
      return ContentTypeFilter.RESEARCH;
    case UserSubscription.CR:
      return ContentTypeFilter.COVENANTS;
    case UserSubscription.LFI:
      return ContentTypeFilter.NEWS;
    default:
      return ContentTypeFilter.ALL;
  }
};

export const getInitialArticleCount = (subscription: UserSubscription): number => {
  switch (subscription) {
    case UserSubscription.CR:
    case UserSubscription.LFI:
    case UserSubscription.CR_LFI:
      return 10;
    default:
      return 5;
  }
};
